import React from 'react';
import { CTASection } from '../../components/CTASection';
import { FAQSection } from '../../components/FAQSection';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { TrendingUp, BookOpen, FileText } from 'lucide-react';
import { useSite } from '../../contexts/SiteContext';

export const ScapulaFracture = () => {
  const { theme } = useSite();
  
  const faqs = [
    { question: 'How serious is a scapula fracture?', answer: 'Scapula fractures require significant force (high-energy trauma) and are often associated with other injuries (90% have associated injuries). However, most scapula fractures heal well without surgery due to the rich surrounding muscle coverage and blood supply.' },
    { question: 'Do scapula fractures need surgery?', answer: 'Only 10-20% of scapula fractures require surgery. Surgery is indicated for: significantly displaced glenoid fractures (intra-articular), severely angulated scapula neck fractures, floating shoulder injuries, or fractures causing shoulder instability.' },
    { question: 'How long does recovery take?', answer: 'Non-operative: Pain improves in 2-4 weeks, sling for 2-4 weeks, gradual motion from 2 weeks, strengthening from 6 weeks, return to activities 3-4 months. Surgical: Similar timeline but may require longer protection initially.' },
    { question: 'Will I have long-term problems?', answer: 'Most patients achieve excellent functional outcomes. Some may have mild residual stiffness or discomfort with heavy activity. Glenoid fractures involving the joint surface have higher risk of post-traumatic arthritis if not anatomically reduced.' }
  ];

  return (
    <div className="min-h-screen" data-testid="scapula-fracture-page">
      <section className="bg-gradient-to-br from-orange-50 to-white py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <span className="inline-block px-4 py-1 rounded-full text-sm font-medium mb-4" style={{ backgroundColor: `${theme?.primaryHex}20`, color: theme?.primaryHex }}>
              Trauma & Fractures
            </span>
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">Scapula (Shoulder Blade) Fracture Treatment in Hyderabad</h1>
            <p className="text-xl text-gray-600">Expert trauma care by Dr. Deepthi Nandan Reddy at Apollo Hospitals</p>
          </div>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Understanding Scapula Fractures</h2>
            <div className="prose prose-lg max-w-none text-gray-700 space-y-4">
              <p>Scapula fractures are uncommon (3-5% of shoulder fractures, 1% of all fractures) because the scapula is protected by thick surrounding muscles. They typically result from high-energy trauma (motor vehicle accidents, falls from height).</p>
              <p><strong>Key Point:</strong> Due to the high-energy mechanism, 90% of patients have associated injuries (rib fractures, pulmonary contusion, clavicle fractures, brachial plexus injury). A thorough trauma evaluation is essential.</p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 mb-8">Classification</h2>
            <div className="grid md:grid-cols-2 gap-6">
              <Card className="border-t-4" style={{ borderTopColor: theme?.primaryHex }}>
                <CardHeader><CardTitle>Body & Spine (Most Common)</CardTitle></CardHeader>
                <CardContent className="text-gray-600">
                  <p>• 50-60% of scapula fractures</p>
                  <p>• Usually from direct blow</p>
                  <p>• Almost always non-operative</p>
                  <p>• Excellent outcomes</p>
                </CardContent>
              </Card>
              <Card className="border-t-4" style={{ borderTopColor: theme?.primaryHex }}>
                <CardHeader><CardTitle>Glenoid Fractures</CardTitle></CardHeader>
                <CardContent className="text-gray-600">
                  <p>• 10% of scapula fractures</p>
                  <p>• Intra-articular (joint surface)</p>
                  <p>• May require surgery if displaced</p>
                  <p>• Risk of arthritis if malunited</p>
                </CardContent>
              </Card>
              <Card className="border-t-4" style={{ borderTopColor: theme?.primaryHex }}>
                <CardHeader><CardTitle>Scapular Neck</CardTitle></CardHeader>
                <CardContent className="text-gray-600">
                  <p>• 25% of scapula fractures</p>
                  <p>• Connects body to glenoid</p>
                  <p>• Surgery if severely angulated</p>
                  <p>• Watch for floating shoulder</p>
                </CardContent>
              </Card>
              <Card className="border-t-4" style={{ borderTopColor: theme?.primaryHex }}>
                <CardHeader><CardTitle>Acromion & Coracoid</CardTitle></CardHeader>
                <CardContent className="text-gray-600">
                  <p>• Less common</p>
                  <p>• Acromion: may narrow subacromial space</p>
                  <p>• Coracoid: check for associated injuries</p>
                  <p>• Surgery if significantly displaced</p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-3 mb-6">
              <TrendingUp className="w-8 h-8" style={{ color: theme?.primaryHex }} />
              <h2 className="text-3xl font-bold text-gray-900">Treatment</h2>
            </div>
            <div className="space-y-6">
              <Card className="border-l-4 border-l-green-500">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <span className="bg-green-100 text-green-800 text-xs px-2 py-1 rounded">80-90% OF CASES</span>
                    Non-Operative Treatment
                  </CardTitle>
                </CardHeader>
                <CardContent className="text-gray-700 space-y-2">
                  <p>• Sling for comfort (2-4 weeks)</p>
                  <p>• Early gentle range of motion</p>
                  <p>• Progressive rehabilitation</p>
                  <p>• Return to activity as tolerated</p>
                  <p className="bg-green-50 p-3 rounded mt-3 text-sm">Outcomes are excellent due to rich muscle coverage providing blood supply and inherent stability.</p>
                </CardContent>
              </Card>
              
              <Card className="border-l-4 border-l-orange-500">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <span className="bg-orange-100 text-orange-800 text-xs px-2 py-1 rounded">10-20% OF CASES</span>
                    Surgical Indications
                  </CardTitle>
                </CardHeader>
                <CardContent className="text-gray-700 space-y-2">
                  <p>• Glenoid fractures with &gt;4mm step-off or &gt;20% articular involvement</p>
                  <p>• Scapular neck with &gt;40° angulation or &gt;25mm medialization</p>
                  <p>• Floating shoulder (combined clavicle + scapula neck fracture)</p>
                  <p>• Acromion fractures narrowing subacromial space</p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      <section className="py-12 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-3 mb-6">
              <FileText className="w-6 h-6 text-gray-500" />
              <h3 className="text-xl font-bold text-gray-700">Scientific References</h3>
            </div>
            <div className="bg-white p-6 rounded-lg text-sm text-gray-600 space-y-2">
              <p>1. Cole PA, et al. Scapula fractures. Orthop Clin North Am. 2013</p>
              <p>2. Zlowodzki M, et al. Operative treatment of acute displaced scapula fractures. J Orthop Trauma. 2006</p>
              <p className="text-xs text-gray-400 mt-4 pt-4 border-t">Last Updated: January 2025 | Content reviewed by Dr. Deepthi Nandan Reddy, FRCS (Ortho)</p>
            </div>
          </div>
        </div>
      </section>

      <FAQSection faqs={faqs} />
      <CTASection />
    </div>
  );
};

export default ScapulaFracture;
